import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Rating } from "@mui/material";
import { dateConvert } from '../../utils/Helper';
import { useMemo } from 'react';
import _ from "underscore";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ViewResponse = ({ viewResponse }) => {
    const totalEvaluators = viewResponse.length;

    const tablesByCategory = useMemo(() => {
        const categoryMap = new Map();

        viewResponse.forEach(evaluation => {
            evaluation.questions_answers.forEach(qa => {
                const categoryName = qa.category || "Uncategorized";
                if (!categoryMap.has(categoryName)) {
                    categoryMap.set(categoryName, new Map());
                }
                const questionMap = categoryMap.get(categoryName);
                if (!questionMap.has(qa.question)) {
                    questionMap.set(qa.question, {
                        question: qa.question,
                        type: qa.type,
                        category: categoryName
                    });
                }
            });
        });

        const finalStructuredData = [];
        categoryMap.forEach((questionMap, categoryName) => {
            const questions = Array.from(questionMap.values());

            const tableRows = questions.map((qObj, index) => {
                const row = {
                    question: qObj.question,
                    type: qObj.type,
                    id: index
                };
                viewResponse.forEach(evalItem => {
                    const qa = evalItem.questions_answers?.find(
                        item => item.question === qObj.question && item.category === categoryName
                    );
                    row[evalItem.evaluator] = qa ? qa.answer : '-';
                });
                if (qObj.type === 'rating') {
                    const scores = viewResponse
                        .map(e => e.questions_answers?.find(
                            qa => qa.question === qObj.question && qa.category === categoryName
                        )?.answer)
                        .map(ans => (ans !== undefined && !isNaN(ans) ? Number(ans) : 0));

                    const sum = scores.reduce((a, b) => a + b, 0);
                    row.mean = totalEvaluators > 0 ? (sum / totalEvaluators).toFixed(2) : '0.00';
                    row.totalScore = sum;
                } else {
                    row.mean = '-';
                }

                return row;
            });

            finalStructuredData.push({
                category: categoryName,
                data: tableRows
            });
        });

        return finalStructuredData;
    }, [viewResponse, totalEvaluators]);

    const calculateEvaluatorTotal = (tableData, evaluatorName) => {
        return tableData.reduce((acc, row) => {
            if (row.type === 'rating' && row[evaluatorName] !== '-') {
                return acc + (Number(row[evaluatorName]) || 0);
            }
            return acc;
        }, 0);
    };

    const calculateCategoryMean = (tableData) => {
        const ratingRows = tableData.filter(row => row.type === 'rating');
        if (ratingRows.length === 0) return '-';

        const grandTotalPoints = ratingRows.reduce((acc, row) => acc + (Number(row.totalScore) || 0), 0);
        return totalEvaluators > 0 ? (grandTotalPoints / totalEvaluators).toFixed(2) : '0.00';
    };

    const getFooterGroup = (tableData) => (
        <ColumnGroup>
            <Row>
                <Column
                    footer="Category Totals / Avg:"
                    className="fw-bold bg-light px-3"
                    style={{ width: '25%' }}
                />
                {_.filter(viewResponse, (item) => item?.category === tableData.category).map((evalItem, index) => (
                    <Column
                        key={`footer_${index}`}
                        footer={calculateEvaluatorTotal(tableData?.data, evalItem.evaluator)}
                        className="text-center fw-bold text-primary bg-light"
                    />
                ))}
                <Column
                    footer={calculateCategoryMean(tableData?.data)}
                    className="text-center fw-bold text-white border-start"
                    style={{ width: '12%', fontSize: '1rem', background: "#0075db" }}
                />
            </Row>
        </ColumnGroup>
    );

    const evaluatorBodyTemplate = (rowData, column) => {
        const val = rowData[column.field];
        if (rowData.type === 'rating' && val !== '-') {
            return (
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <span className='text-primary fw-semibold' style={{ fontSize: "16px" }}>{val}</span>
                    <Rating value={Number(val) || 0} readOnly size="small" />
                </div>
            );
        }
        if (rowData?.type === "date") {
            return (
                <div className='text-primary fw-semibold d-flex align-items-center justify-content-center'>
                    {dateConvert(val)}
                </div>
            );
        }
        return (
            <div className="d-flex align-items-center justify-content-center py-2" >
                <span>{val}</span>
            </div>
        );
    };

    const grandAverage = useMemo(() => {
        let totalPoints = 0;
        tablesByCategory.forEach(cat => {
            cat.data.forEach(row => {
                if (row.type === 'rating') {
                    totalPoints += Number(row.totalScore) || 0;
                }
            });
        });
        return totalEvaluators > 0 ? (totalPoints / totalEvaluators).toFixed(2) : '0.00';
    }, [tablesByCategory, totalEvaluators]);

    return (
        <div className="container-fluid py-3">
            <div className='px-4 py-2 d-flex justify-content-between  shadow align-items-center mb-4 rounded'>
                <div className='d-flex gap-3 align-items-center'>
                    <div style={{ position: 'relative' }}>
                        <img src='./assets/man.png' alt='' width={45} height={45}
                            className='rounded-circle border border-2 border-white shadow-sm' />
                    </div>
                    <div >
                        <p> <span className='fw-bolder' style={{ fontSize: "18px", color: '#1a1a1a', lineHeight: 1 }}>
                            {viewResponse?.[0]?.target_user}
                        </span></p>
                        <p>{viewResponse?.[0]?.target_role_on_project}</p>
                    </div>
                </div>
                <div className='text-center' style={{ lineHeight: "20px" }}>
                    <div className='text-muted small fw-semibold' >OVERALL RATING</div>
                    <div className='fw-bold d-inline-flex align-items-center' style={{ fontSize: "22px" }}>
                        {grandAverage} <span className='ms-1' style={{ color: '#ffc107', fontSize: '16px' }}><FontAwesomeIcon icon="fa-solid fa-star" /></span>
                    </div>
                    <p className='fw-semibold'>
                        <small>Based on {totalEvaluators} evaluator</small>
                    </p>
                </div>
            </div>

            {tablesByCategory.map((categoryObj, catIndex) => (
                <div key={catIndex} className="card mb-4 shadow-sm border-0 bg-white">
                    <div className="card-header bg-light border-0 py-3">
                        <h5 className="mb-0 text-balck text-uppercase fw-bold" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>
                            {categoryObj.category}
                        </h5>
                    </div>
                    <DataTable
                        value={categoryObj.data}
                        stripedRows
                        responsiveLayout="scroll"
                        footerColumnGroup={getFooterGroup(categoryObj)}
                        className="table-sm">
                        <Column
                            field="question"
                            header="Questions"
                            className="fw-bold text-capitalize px-3"
                            style={{ width: '25%' }}
                        />
                        {_.filter(viewResponse, (item) => item?.category === categoryObj.category)?.map((evalItem, index) => (
                            <Column
                                key={evalItem.evaluator || index}
                                field={evalItem.evaluator}
                                style={{ textAlign: "center", minHeight: '40px' }}
                                header={evalItem.evaluator}
                                body={evaluatorBodyTemplate}
                                className="text-center"
                            />
                        ))}
                        <Column
                            field="mean"
                            header="Mean Score"
                            style={{ width: '12%', textAlign: "center", background: "#a6d5fa38" }}
                            className="border-start"
                            body={(rowData) => (
                                <div className="d-flex flex-column align-items-center justify-content-center " style={{ color: "#0075db" }}>
                                    {rowData.type === 'rating' ? (
                                        <span className="fw-bold">{rowData.mean}</span>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            ))}
        </div>
    );
};

export default ViewResponse;